/**
 * Meter ballistics: how the raw per-window readings from the audio engine
 * become the bars a person can read. Peaks rise instantly and fall at a fixed
 * rate; the peak-hold line stays for a second and then falls; RMS is smoothed.
 */
import type { LevelReading } from '../../../audio/api';

export const METER_FLOOR_DB = -60;
export const WARN_DB = -20;
export const DANGER_DB = -9;
export const PEAK_DECAY_DB_PER_S = 24;
export const HOLD_MS = 1000;
export const HOLD_DECAY_DB_PER_S = 30;
export const RMS_RELEASE_MS = 300;
export const CLIP_HOLD_MS = 2000;

export interface ChannelState {
  /** Displayed peak, dBFS. */
  peak: number;
  /** Displayed (smoothed) RMS, dBFS. */
  rms: number;
  /** Peak-hold line, dBFS. */
  hold: number;
  /** Time since the hold line was last pushed up, ms. */
  holdAge: number;
}

export interface MeterState {
  channels: ChannelState[];
  /** Time since the last clip, ms; null when the indicator is off. */
  clipAge: number | null;
}

export function createMeterState(): MeterState {
  return { channels: [], clipAge: null };
}

const floor = (db: number | undefined): number =>
  db === undefined || Number.isNaN(db) ? METER_FLOOR_DB : Math.max(METER_FLOOR_DB, Math.min(db, 6));

/** Advance the display state by `dtMs` given the newest reading. Mutates and returns `state`. */
export function stepMeter(state: MeterState, reading: LevelReading | undefined, dtMs: number): MeterState {
  const dt = Math.max(0, Math.min(dtMs, 250));
  const count = reading ? Math.max(reading.peakDb.length, reading.rmsDb.length) : state.channels.length;
  while (state.channels.length < count) {
    state.channels.push({ peak: METER_FLOOR_DB, rms: METER_FLOOR_DB, hold: METER_FLOOR_DB, holdAge: HOLD_MS });
  }
  state.channels.length = count;

  // One-pole release towards the incoming RMS; attack is immediate.
  const release = 1 - Math.exp(-dt / RMS_RELEASE_MS);
  for (let c = 0; c < count; c++) {
    const channel = state.channels[c]!;
    const peakIn = floor(reading?.peakDb[c]);
    const rmsIn = floor(reading?.rmsDb[c]);

    channel.peak = Math.max(peakIn, channel.peak - (PEAK_DECAY_DB_PER_S * dt) / 1000, METER_FLOOR_DB);
    channel.rms = rmsIn >= channel.rms ? rmsIn : channel.rms + (rmsIn - channel.rms) * release;

    if (peakIn >= channel.hold) {
      channel.hold = peakIn;
      channel.holdAge = 0;
    } else {
      channel.holdAge += dt;
      if (channel.holdAge > HOLD_MS) {
        channel.hold = Math.max(channel.peak, channel.hold - (HOLD_DECAY_DB_PER_S * dt) / 1000, METER_FLOOR_DB);
      }
    }
  }

  if (reading?.clipped) state.clipAge = 0;
  else if (state.clipAge !== null) {
    state.clipAge += dt;
    if (state.clipAge >= CLIP_HOLD_MS) state.clipAge = null;
  }
  return state;
}

/** Position of a level on the meter scale: 0 at the floor, 1 at 0 dBFS. */
export function dbToMeter(db: number): number {
  if (!(db > METER_FLOOR_DB)) return 0;
  return Math.min(1, (db - METER_FLOOR_DB) / -METER_FLOOR_DB);
}

export type MeterZone = 'green' | 'yellow' | 'red';

export function zoneOf(db: number): MeterZone {
  return db >= DANGER_DB ? 'red' : db >= WARN_DB ? 'yellow' : 'green';
}

/** True once every bar has fallen back to the floor, so drawing can be skipped. */
export function isIdle(state: MeterState): boolean {
  return state.clipAge === null && state.channels.every((c) => c.peak <= METER_FLOOR_DB && c.hold <= METER_FLOOR_DB && c.rms <= METER_FLOOR_DB + 0.05);
}
